import { LegalDocument } from './LegalDocument';
import { SITE } from '../data/site';

export function PrivacyPolicy() {
  return (
    <LegalDocument
      title="Privacy Policy"
      intro={`How ${SITE.name} collects, uses, and protects your information when you play the arcade.`}
      lastUpdated={SITE.legalLastUpdated}
      sections={[
        {
          heading: 'Who we are',
          body: (
            <p>
              {SITE.name} is operated by {SITE.legalEntity} (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or
              &ldquo;our&rdquo;). {SITE.name} is a browser-based arcade — a Progressive Web App you
              can install and play without downloading anything from an app store. This policy
              covers the marketing site ({SITE.marketingUrl}) and the arcade app ({SITE.appUrl}).
            </p>
          ),
        },
        {
          heading: 'Information we collect',
          body: (
            <>
              <p>We keep collection deliberately small. Depending on how you use the arcade, we may collect:</p>
              <ul>
                <li>
                  <strong>Account information.</strong> If you create an account, your email address
                  and a display name or initials for the leaderboards.
                </li>
                <li>
                  <strong>A device identifier.</strong> An anonymous, signed token stored in a
                  first-party cookie (<code>a84_did</code>) that lets us meter free plays and prevent
                  abuse without knowing who you are.
                </li>
                <li>
                  <strong>Gameplay telemetry.</strong> Anonymous run statistics — how long a run
                  lasted, average frame rate, level reached, and how a run ended — used to balance
                  games and catch bugs. This lives in our own database, not a third-party analytics
                  service.
                </li>
                <li>
                  <strong>Billing information.</strong> If you buy a Pass, our payment processor
                  (PayPal) handles your card or account details. We never see or store your full
                  payment credentials — only a transaction reference tied to your entitlement.
                </li>
              </ul>
            </>
          ),
        },
        {
          heading: 'How we use it',
          body: (
            <>
              <p>We use the information above to:</p>
              <ul>
                <li>Run the arcade — launch games, save high scores, and track your Pass entitlements.</li>
                <li>Meter free plays fairly and protect against automated abuse.</li>
                <li>Balance difficulty, fix bugs, and decide which cartridges to build next.</li>
                <li>Process payments and honor the Pass you purchased.</li>
                <li>Respond when you contact us for support.</li>
              </ul>
              <p>
                We do <strong>not</strong> sell your personal information, and we do not run
                third-party advertising SDKs inside the arcade.
              </p>
            </>
          ),
        },
        {
          heading: 'Analytics & advertising',
          body: (
            <p>
              Gameplay telemetry is stored in our own infrastructure and used only in aggregate.
              Any promotional banners shown inside the arcade are our own house ads (for our games
              and Passes); we do not embed programmatic ad networks or their tracking tags in the
              app itself.
            </p>
          ),
        },
        {
          heading: 'Cookies',
          body: (
            <p>
              We use a small number of first-party cookies that are essential to running the arcade —
              notably the signed device identifier and your login session. We describe them in detail
              in our <a href="/cookies">Cookie Policy</a>.
            </p>
          ),
        },
        {
          heading: 'Sharing & processors',
          body: (
            <>
              <p>We share data only with the service providers that make the arcade work:</p>
              <ul>
                <li>
                  <strong>PayPal</strong> — to process Pass payments and subscriptions.
                </li>
                <li>
                  <strong>Our hosting &amp; content-delivery providers</strong> — to serve the app and
                  store account, score, and telemetry data.
                </li>
              </ul>
              <p>
                We may also disclose information if required by law, or to protect the rights, safety,
                and integrity of the arcade and its players.
              </p>
            </>
          ),
        },
        {
          heading: 'Data retention',
          body: (
            <p>
              We keep account and entitlement data for as long as your account is active. Anonymous
              telemetry is retained in aggregate for product analysis. You can ask us to delete your
              account and associated personal data at any time — see below.
            </p>
          ),
        },
        {
          heading: 'Your rights',
          body: (
            <p>
              Depending on where you live, you may have the right to access, correct, export, or
              delete your personal information, and to object to certain processing. To exercise any
              of these, email us at{' '}
              <a href={`mailto:${SITE.emails.support}`}>{SITE.emails.support}</a> and we&rsquo;ll take
              care of it.
            </p>
          ),
        },
        {
          heading: "Children's privacy",
          body: (
            <p>
              The arcade is a general-audience product and is not directed to children under 13. We do
              not knowingly collect personal information from children under 13. If you believe a child
              has provided us personal information, contact us and we will delete it.
            </p>
          ),
        },
        {
          heading: 'Changes to this policy',
          body: (
            <p>
              We may update this policy as the arcade evolves. When we make material changes, we&rsquo;ll
              revise the &ldquo;Last updated&rdquo; date above and, where appropriate, notify you in the app.
            </p>
          ),
        },
        {
          heading: 'Contact',
          body: (
            <p>
              Questions about privacy? Email{' '}
              <a href={`mailto:${SITE.emails.support}`}>{SITE.emails.support}</a> or use our{' '}
              <a href="/contact">contact page</a>.
            </p>
          ),
        },
      ]}
    />
  );
}
